import type {
  Member,
  MemberAccountUpdateRequest,
  MemberChangePasswordRequest,
  MemberLoginRequest,
  MemberLoginResponse,
  MemberRegisterRequest,
} from "@features/authentication/types";

import { authService, memberService } from "@features/authentication/api";
import { useMemberStore } from "@features/authentication/stores";
import { countriesService } from "@features/countries/api";
import { useCartStore, useOrderStore } from "@features/shopping/stores";
import { handleApiError } from "@shared/utils/error";
import { defineStore } from "pinia";

type AuthState = {
  authenticated: boolean;
  error: null | string;
  loading: boolean;
  user: Member | null;
};

export const useAuthStore = defineStore("auth", {
  actions: {
    // Change password
    async changePassword(data: MemberChangePasswordRequest) {
      this.error = null;

      try {
        const response = await authService.changePassword(data);

        if (!response.success) {
          this.error = response.message;
          return;
        }

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Failed to change password");
        throw err;
      }
    },

    clearError() {
      this.error = null;
    },

    // Initialize authentication state
    async initializeAuth(memberId?: string): Promise<Member | null> {
      this.loading = true;
      this.error = null;

      try {
        const userId =
          memberId ?? localStorage.getItem("member_id") ?? sessionStorage.getItem("member_id");

        if (!userId) {
          this.user = null;
          this.authenticated = false;

          return null;
        }

        const details = await memberService.getAccountDetails(userId);

        if (!details.success) {
          this.error = details.message;
          return null;
        }

        if (!details.member_id) {
          this.error = "Failed to fetch current user";
          return null;
        }

        const cartStore = useCartStore();
        const orderStore = useOrderStore();

        try {
          const [addressResult, cartResult, countryCode] = await Promise.allSettled([
            memberService.getAccountDetails(userId),
            cartStore.initializeCart(),
            getCountryCode(userId),
          ]);

          await orderStore.initializeOrders(userId);

          if (addressResult.status === "rejected") {
            console.warn("[Auth] Address fetch failed:", addressResult.reason);
          }
          if (cartResult.status === "rejected") {
            console.warn("[Auth] Cart initialization failed:", cartResult.reason);
          }

          const resolvedCountryCode = countryCode.status === "fulfilled" ? countryCode.value : null;

          const user: Member = {
            ...details,
            country_code: resolvedCountryCode ?? undefined,
            member_birth_day: details.member_birth_day ?? "",
            member_id: details.member_id,
          };

          this.user = user;
          this.authenticated = true;

          return user;
        } catch (initError) {
          console.error("[Auth] Initialization failed, rolling back auth state:", initError);
          this.user = null;
          this.authenticated = false;
          throw initError;
        }
      } catch (err) {
        this.error = handleApiError(err, "Failed to initialize authentication");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    // Login function
    async login(
      credentials: MemberLoginRequest,
      rememberMe: boolean,
    ): Promise<MemberLoginResponse> {
      this.loading = true;
      this.error = null;

      try {
        const cartStore = useCartStore();
        const response = await authService.login(credentials);

        if (!response.success) {
          this.error = response.message;
          return response;
        }

        const storage = rememberMe ? localStorage : sessionStorage;
        storage.setItem("member_id", response.member_id);
        localStorage.removeItem("guest_id");
        sessionStorage.removeItem("guest_id");

        await this.initializeAuth(response.member_id);
        await cartStore.transferCart(response.member_id);

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Login failed");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    // Logout function
    logout() {
      try {
        localStorage.removeItem("member_id");
        sessionStorage.removeItem("member_id");
        this.user = null;
        this.authenticated = false;

        const memberStore = useMemberStore();
        memberStore.clearMember();

        const cartStore = useCartStore();
        cartStore.clearCart();

        const orderStore = useOrderStore();
        orderStore.clearOrder();
      } catch (err) {
        console.error("[Auth Store] Logout failed:", err);
      }
    },

    // Register function
    async register(credentials: MemberRegisterRequest) {
      this.loading = true;
      this.error = null;

      try {
        const cartStore = useCartStore();
        const response = await authService.register(credentials);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        localStorage.setItem("member_id", response.member_id);
        localStorage.removeItem("guest_id");
        sessionStorage.removeItem("guest_id");

        await this.initializeAuth(response.member_id);
        await cartStore.transferCart(response.member_id);

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Registration failed");
        throw err;
      } finally {
        this.loading = false;
      }
    },

    // Update account details
    async updateAccountDetails(data: MemberAccountUpdateRequest) {
      this.error = null;

      try {
        const response = await memberService.updateAccountDetails(data);

        if (!response.success) {
          this.error = response.message;
          return null;
        }

        const details = await memberService.getAccountDetails(data.member_id);

        if (!details.success) {
          this.error = details.message;
          return null;
        }

        if (!details.member_id) {
          this.error = "Failed to fetch current user";
          return null;
        }

        const countryCode = await getCountryCode(data.member_id);

        const user: Member = {
          ...details,
          country_code: countryCode ?? undefined,
          member_birth_day: details.member_birth_day ?? "",
          member_id: details.member_id,
        };

        this.user = user;
        this.authenticated = true;

        return response;
      } catch (err) {
        this.error = handleApiError(err, "Failed to update account details");
        throw err;
      }
    },
  },

  getters: {
    currentUser: (state) => state.user,
    isAuthenticated: (state) => state.authenticated,
  },

  state: (): AuthState => ({
    authenticated: false,
    error: null,
    loading: false,
    user: null,
  }),
});

// Helper to get country code with robust error handling
const getCountryCode = async (memberId: string): Promise<null | string> => {
  try {
    if (!memberId) {
      console.warn("[Auth] Cannot get country code: missing member ID");
      return null;
    }

    const memberStore = useMemberStore();
    const addresses = await memberStore.getAddress(memberId);

    if (!addresses.success) {
      console.warn("[Auth] Failed to fetch member addresses for country resolution");
      return null;
    }

    const { billing_address, shipping_address } = addresses;

    if (!billing_address && !shipping_address) {
      console.warn("[Auth] No addresses found for country resolution");
      return null;
    }

    let countries;
    try {
      countries = await countriesService.getCountries();
    } catch (countryServiceError) {
      console.error("[Auth] Failed to fetch countries list:", countryServiceError);
      return null;
    }

    if (!countries.countries || !Array.isArray(countries.countries)) {
      console.error("[Auth] Invalid countries response from service");
      return null;
    }

    const countryName = billing_address?.billing_region ?? shipping_address?.shipping_region;

    let country = countries.countries.find(
      (country) => country && country.country_name === countryName,
    );

    // Handle old data where region is "China" instead of "China (Mainland)"
    if (!country && countryName === "China") {
      country = countries.countries.find(
        (country) => country && country.country_name === "China (Mainland)",
      );
    }

    if (!country?.country_code) {
      console.warn(`[Auth] Country code not found for country: ${countryName}`);
      return null;
    }

    return country.country_code;
  } catch (error) {
    console.error("[Auth] Unexpected error in country code resolution:", error);
    return null;
  }
};
